import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { View, Keyboard, Text } from 'react-native';
import { useUnistyles } from 'react-native-unistyles';
import { useMutation } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import CheckinTableView from '@/components/Events/CheckinTableView';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import { ActivityIndicator } from 'react-native-paper';
import Toast from 'react-native-toast-message';
import { DateTime } from 'luxon';
import { useLocalSearchParams, useRouter, useNavigation, usePathname } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import PageTitle from '@/components/General/PageTitle';
import usePlatform from '@/utils/hooks/usePlatform';
import useKioskStore from '@/data/store/useKioskStore';
import useLabel from '@/utils/hooks/useLabel';
import ChildLabelView from '@/components/Printing/ChildLabelView';
import FamilyLabelView from '@/components/Printing/FamilyLabelView';
import PrinterSelectSheet from '@/components/Printing/PrinterSelectSheet';
import { exportPeopleData, updateBarcodes } from '@/data/people';
import { updateAnonymousHeadCount } from '@/data/events';
import { showAlert } from '@/utils/showAlert';
import { queryClient } from '@/data/client';
import { QueryKeys } from '@/utils/react-query/QueryKeys';

import useCheckinData from './_checkin/useCheckinData';
import useCheckinActions from './_checkin/useCheckinActions';
import useCheckinRealtime from './_checkin/useCheckinRealtime';
import CheckinToolbar from './_checkin/CheckinToolbar';
import CheckinKiosk from './_checkin/CheckinKiosk';
import CheckinCardGrid from './_checkin/CheckinCard';
import {
    PreviewModal,
    KioskPinModal,
    BarcodeModal,
    QuickAddModal,
    CheckinPrintSheetModal,
    CheckinExportModal,
    AnonymousHeadCountModal,
} from './_checkin/CheckinModals';

const generatedCodes = [];

const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 5; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    if (generatedCodes.includes(code)) return generateCode();
    generatedCodes.push(code);
    return code;
};

const CHECKIN_EXPORT_FIELDS = [
    { key: 'checkedIn', label: 'Checked In' },
    { key: 'checkedInLocation', label: 'Checked In Location' },
    { key: 'checkedInTime', label: 'Checked In Time' },
    { key: 'checkedOut', label: 'Checked Out' },
    { key: 'checkedOutTime', label: 'Checked Out Time' },
];

export default function CheckInScreen() {
    const { eventInstanceID, newPersonID, newFamilyMemberIDs } = useLocalSearchParams();
    const { isLandscape } = usePlatform();
    const pathname = usePathname();
    const router = useRouter();
    const navigation = useNavigation();

    // --- Modal state ---
    const [previewOpen, setPreviewOpen] = useState(false);
    const [pinOpen, setPinOpen] = useState(false);
    const [barcodeOpen, setBarcodeOpen] = useState(false);
    const [quickAddOpen, setQuickAddOpen] = useState(false);
    const [printSheetOpen, setPrintSheetOpen] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [anonymousHeadCountModalOpen, setAnonymousHeadCountModalOpen] = useState(false);
    const [devTestPrinter, setDevTestPrinter] = useState(false);

    // --- Label queue & view state ---
    const [labelQueue, setLabelQueue] = useState([]);
    const [labelPreviews, setLabelPreviews] = useState(null);
    const [checkinLocation, setCheckinLocation] = useState("");
    const [viewType, setViewType] = useState("cards");

    // --- Barcode state ---
    const [readyToScan, setReadyToScan] = useState(false);
    const [barcode, setBarcode] = useState("");
    const [peopleForNewBarcode, setPeopleForNewBarcode] = useState([]);
    const barcodeRef = useRef(null);

    // --- Kiosk state ---
    const [kioskPin, setKioskPin] = useState("");
    const [kioskPinForUnlock, setKioskPinForUnlock] = useState("");
    const { kioskModeEnabled, lockKioskMode, unlockKioskMode, unlockCode: storedUnlockCode } = useKioskStore();

    // --- Realtime toggle state ---
    const [realtimeEnabled, setRealtimeEnabled] = useState(true);



    // --- Redirect if no eventInstanceID ---
    useEffect(() => {
        if (!eventInstanceID) router.back();
    }, [eventInstanceID]);

    // --- Theme ---
    const { theme: { colors, sideNavWidth }, rt: { screen } } = useUnistyles();
    const LIST_COLUMNS = useMemo(() => {
        const effectiveWidth = kioskModeEnabled ? screen.width : screen.width - sideNavWidth;
        return effectiveWidth > 1000 ? 3 : effectiveWidth > 500 ? 2 : 1;
    }, [kioskModeEnabled, screen.width, sideNavWidth]);

    // --- Data hook ---
    const {
        eventInstance, eventInstanceLoading, attendance, fetchingAttendance,
        availablePeople, setAvailablePeople,
        searchText, setSearchText,
        selectedFilter, setSelectedFilter,
        totalCount, isFetching, isLoading, fetchNextPage, hasNextPage,
        refreshList, filterStore, everyoneFilters, everyoneFilterLogic,
    } = useCheckinData({ eventInstanceID, checkinLocation, labelQueue });

    // --- Queries ---
    const { data: loggedInUser } = Query.people.loggedInUser();

    // --- Printing ---
    const isPrintingAvailable = checkinLocation?.printLabelID || checkinLocation?.printFamilyLabelID;
    const {
        printers, printChildLabel, printParentLabel, previewChildLabel, previewParentLabel,
        // Native-only exports for Brother printer:
        selectedPrinter, isDiscovering, showPrinterSheet, setShowPrinterSheet,
        childLabelRef, familyLabelRef, childLabelData, familyLabelData,
        openPrinterSheet, handleSelectPrinter, connectToDirectWifi, connectByIP, discoverPrinters,
    } = useLabel({ isPrintingAvailable });
    const realPrinters = useMemo(() => printers.filter(p => p.isConnected), [printers]);
    const availablePrinters = useMemo(() => devTestPrinter ? [{ name: 'Dev Test Printer', isConnected: true }] : realPrinters, [realPrinters, devTestPrinter]);

    // --- Actions hook ---
    const {
        checkIn, checkOut, addListAttendance, removeListAttendance, updateListAttendance,
        addAttendanceMutation, removeAttendanceMutation, markAttendancePrintedMutation,
        addPersonToLabelQueue, removePersonFromLabelQueue, clearAllPending,
    } = useCheckinActions({ eventInstance, setAvailablePeople, checkinLocation, loggedInUser, selectedFilter });

    // --- Realtime ---
    const connectionLostTimeoutRef = useRef(null);
    const isShowingConnectionLostRef = useRef(false);

    const handleConnectionLost = useCallback(() => {
        // Clear any existing timeout
        if (connectionLostTimeoutRef.current) {
            clearTimeout(connectionLostTimeoutRef.current);
        }

        // Set a 3-second delay before showing the toast
        connectionLostTimeoutRef.current = setTimeout(() => {
            isShowingConnectionLostRef.current = true;
            Toast.show({
                type: 'error',
                text1: 'Connection Lost',
                text2: 'Realtime connection interrupted. You may miss live updates.',
                position: 'top',
            });
        }, 3000);
    }, []);

    const handleConnectionRestored = useCallback(() => {
        // Clear the timeout if connection is restored within 3 seconds
        if (connectionLostTimeoutRef.current) {
            clearTimeout(connectionLostTimeoutRef.current);
            connectionLostTimeoutRef.current = null;
        }

        // Only show restored message if we were showing the lost message
        if (isShowingConnectionLostRef.current) {
            isShowingConnectionLostRef.current = false;
            Toast.show({
                type: 'info',
                text1: 'Connection Restored',
                text2: 'Realtime connection is back. Tap to refresh for latest data.',
                position: 'top',
                onPress: () => {
                    refreshList();
                    Toast.hide();
                },
            });
        }
    }, [refreshList]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (connectionLostTimeoutRef.current) {
                clearTimeout(connectionLostTimeoutRef.current);
            }
        };
    }, []);

    const { isConnected } = useCheckinRealtime({
        eventInstance,
        eventInstanceLoading,
        addListAttendance,
        removeListAttendance,
        updateListAttendance,
        onConnectionLost: handleConnectionLost,
        onConnectionRestored: handleConnectionRestored,
        selectedFilter,
        realtimeEnabled,
    });

    const handleToggleRealtime = useCallback(() => {
        if (realtimeEnabled) {
            setRealtimeEnabled(false);
        } else {
            setRealtimeEnabled(true);
            refreshList();
        }
    }, [realtimeEnabled, refreshList]);

    // --- Set default location ---
    useEffect(() => {
        if (!eventInstanceLoading && eventInstance?.Locations?.length > 0) {
            const defaultLocationID = eventInstance?.Event?.defaultLocationID;
            const defaultLocation = defaultLocationID ? eventInstance.Locations.find(l => l.id === defaultLocationID) : null;
            const locationToSet = defaultLocation || eventInstance.Locations[0];
            setCheckinLocation(locationToSet);
            if (locationToSet?.CheckInFilter) setSelectedFilter("Suggested");
        }
    }, [eventInstanceLoading]);

    // --- New person auto-checkin ---
    const processedNewPersonRef = useRef(null);
    useEffect(() => {
        if (newPersonID && eventInstance?.id && !eventInstanceLoading && checkinLocation?.id) {
            if (processedNewPersonRef.current === newPersonID) return;
            processedNewPersonRef.current = newPersonID;
            const locationID = checkinLocation.id;
            const allIDs = [newPersonID, ...(newFamilyMemberIDs ? newFamilyMemberIDs.split(',').filter(Boolean) : [])];
            allIDs.forEach(personID => {
                addAttendanceMutation.mutate({ eventInstanceID: eventInstance.id, personID, submitterID: loggedInUser?.id, locationID });
            });
        }
    }, [newPersonID, newFamilyMemberIDs, eventInstance?.id, eventInstanceLoading, checkinLocation?.id, loggedInUser?.id, addAttendanceMutation]);

    // --- Barcode mutation ---
    const updateBarcodesMutation = useMutation({
        mutationFn: updateBarcodes,
        onSuccess: () => {
            Toast.show({ type: "success", text2: "New barcode has been added!" });
            setBarcodeOpen(false);
            setPeopleForNewBarcode([]);
            setBarcode("");
        },
    });

    // --- Anonymous headcount mutation ---
    const updateAnonymousHeadCountMutation = useMutation({
        mutationFn: ({ anonymousHeadCount }) => updateAnonymousHeadCount({ eventInstanceID: eventInstance?.id, anonymousHeadCount }),
        onSuccess: () => {
            Toast.show({ type: "success", text2: "Anonymous headcount updated!" });
            queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.byId(eventInstance?.id) });
        },
    });



    const openQuickAddPersonModal = useCallback(() => {
        setQuickAddOpen(true);
        Keyboard.dismiss();
    }, []);

    const handleBarcodePress = useCallback(() => setBarcodeOpen(true), []);
    const handleExitKioskPress = useCallback(() => setPinOpen(true), []);

    // --- Header setup ---
    useEffect(() => {
        navigation.setOptions({
            headerShown: !kioskModeEnabled,
            headerTitle: () => (
                <PageTitle
                    title={(eventInstance?.nameOverride || eventInstance?.Event?.name || "Event") + " Check In"}
                    subtitle={!eventInstance?.startTime ? "" : DateTime.fromISO(eventInstance.startTime).toLocaleString(DateTime.DATETIME_MED)}
                />
            ),
            headerRight: () => {
                const menuOptions = [];
                if (loggedInUser?.canAny?.(["Create People"])) {
                    menuOptions.push({ name: "Quick Add Person", icon: dropdownIcons.add, onSelect: openQuickAddPersonModal });
                    menuOptions.push({ name: "Full Person Form", icon: dropdownIcons.add, onSelect: () => { router.push({ pathname: "/people/create", params: { from: pathname } }); Keyboard.dismiss(); } });
                }
                if (loggedInUser?.canAny?.(["Check In"]) && eventInstance?.registrationEnabled) {
                    menuOptions.push({ name: "Scan Registration QR", icon: dropdownIcons.barcode, onSelect: () => router.push({ pathname: `/events/instances/${eventInstanceID}/scan-registration-qr`, params: { defaultLocationID: checkinLocation?.id ?? '' } }) });
                }
                if (loggedInUser?.canAny?.(["Edit People"]) && isLandscape) {
                    menuOptions.push({ name: "Add a Barcode", icon: dropdownIcons.barcode, onSelect: () => setBarcodeOpen(true) });
                }
                if (isLandscape) {
                    menuOptions.push({ name: kioskModeEnabled ? "Disable Kiosk Mode" : "Enable Kiosk Mode", icon: kioskModeEnabled ? dropdownIcons.unlock : dropdownIcons.lock, onSelect: () => setPinOpen(true) });
                }
                if (selectedFilter !== "Recent") {
                    menuOptions.push({ name: "Export to CSV", icon: dropdownIcons.export, onSelect: () => setExportModalOpen(true) });
                }
                menuOptions.push({ name: "Print Check-In Sheet", icon: dropdownIcons.print, onSelect: () => setPrintSheetOpen(true) });
                return <DropdownMenu testID="HeaderMenu" options={menuOptions} />;
            },
        });
    }, [eventInstance, kioskModeEnabled, isLandscape, totalCount, searchText, filterStore.peopleFilters, filterStore.filterLogic, selectedFilter]);

    // --- Export handler ---
    const handleExport = useCallback((selectedFields) => {
        // Match by Location.name — attendance records only embed Locations(name),
        // consistent with the snapshot/processData scoping in useCheckinData.
        const locationAttendanceIDs = checkinLocation?.name
            ? (attendance || []).filter(a => a.Location?.name === checkinLocation.name).map(a => a.attendeeID)
            : null;

        let filters;
        let filterLogic;
        if (["Everyone", "Checked In", "Not Checked In"].includes(selectedFilter)) {
            filters = { ...everyoneFilters };
            filterLogic = everyoneFilterLogic;
        } else if (selectedFilter === "Suggested") {
            filters = { ...(checkinLocation?.CheckInFilter?.filterJSON || {}) };
            filterLogic = checkinLocation?.CheckInFilter?.logic;
        } else {
            return;
        }

        // Rebuild the attendance predicates from live attendance so the export
        // matches the on-screen list even between server snapshot refreshes.
        // "Checked In" scopes to the selected location (all attendance when
        // "All Locations" is selected); "Not Checked In" excludes anyone
        // checked in anywhere. "Everyone" needs no attendance predicate.
        if (selectedFilter === "Checked In") {
            filters.id = locationAttendanceIDs ?? (attendance || []).map(a => a.attendeeID);
            filterLogic = "AND";
        } else if (selectedFilter === "Not Checked In") {
            filters.notid = (attendance || []).map(a => a.attendeeID);
            filterLogic = "AND";
        }

        // Build export filename: reach_checkin_<eventname>_<eventdate>.csv
        const eventName = (eventInstance?.nameOverride || eventInstance?.Event?.name || "event")
            .replace(/[^a-zA-Z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "");
        const eventDate = eventInstance?.startTime
            ? DateTime.fromISO(eventInstance.startTime).toISODate()
            : DateTime.now().toISODate();
        const filename = `reach_checkin_${eventName}_${eventDate}`;

        exportPeopleData({
            totalCount, searchTerm: searchText, filters, filterLogic, selectedFields,
            attendance, filename,
        });
    }, [selectedFilter, totalCount, searchText, everyoneFilters, everyoneFilterLogic, checkinLocation, attendance, eventInstance]);

    // --- Location-conflict check helper ---
    const isDifferentLocation = useCallback((profile) => {
        if (!profile.isSelected) return false;
        if (!profile.checkInLocation) return false;
        if (!checkinLocation?.name) return false;
        return profile.checkInLocation !== checkinLocation.name;
    }, [checkinLocation]);

    const moveToCurrentLocation = useCallback((profile, mainFamilyMember = null) => {
        const locationID = checkinLocation?.id || eventInstance?.Locations?.[0]?.id;
        setAvailablePeople(prev => prev.map(p => p.id === profile.id ? { ...p, isProcessing: true } : p));
        removeAttendanceMutation.mutate(
            { attendeeID: profile?.id, eventInstanceID: eventInstance?.id, mainFamilyMember },
            {
                onSuccess: () => {
                    addAttendanceMutation.mutate({
                        eventInstanceID: eventInstance?.id,
                        personID: profile?.id,
                        submitterID: loggedInUser?.id,
                        locationID,
                        mainFamilyMember,
                    });
                },
            }
        );
    }, [checkinLocation, eventInstance, loggedInUser?.id, removeAttendanceMutation, addAttendanceMutation, setAvailablePeople]);

    const showLocationConflictAlert = useCallback((profile, mainFamilyMember = null) => {
        const personName = `${profile.firstName} ${profile.lastName}`;
        showAlert({
            title: 'Different Location',
            subtitle: `${personName} is currently checked into ${profile.checkInLocation}. What would you like to do?`,
            cancelText: 'Cancel',
            confirmText: 'Remove Check In',
            isDestructive: true,
            onConfirm: () => checkIn(profile, true, profile.isCheckedOut, mainFamilyMember),
            extraButton: {
                text: `Check into ${checkinLocation.name}`,
                onPress: () => moveToCurrentLocation(profile, mainFamilyMember),
            },
        });
    }, [checkinLocation, checkIn, moveToCurrentLocation]);

    // --- Card/row press handlers ---
    const isAwaitingPrintMode = selectedFilter === "Awaiting Print";

    // Track whether we've already warned this session about checking in with no location
    const allLocationsWarnedRef = useRef(false);

    const withAllLocationsWarning = useCallback((proceed) => {
        const hasLocations = (eventInstance?.Locations?.length || 0) > 0;
        if (hasLocations && !checkinLocation && !allLocationsWarnedRef.current) {
            allLocationsWarnedRef.current = true;
            showAlert({
                title: 'No Location Selected',
                subtitle: 'You have "All Locations" selected. This person will be checked in but will not be associated with any location.',
                cancelText: 'Cancel',
                confirmText: 'Continue',
                onConfirm: proceed,
            });
            return;
        }
        proceed();
    }, [checkinLocation, eventInstance?.Locations?.length]);

    const onProfilePress = useCallback((profile) => {
        if (isAwaitingPrintMode) {
            profile.isPending ? removePersonFromLabelQueue(profile, setLabelQueue) : addPersonToLabelQueue(profile, setLabelQueue);
        } else if (checkinLocation?.printLabelID && !profile.isSelected) {
            profile.isPending ? removePersonFromLabelQueue(profile, setLabelQueue) : addPersonToLabelQueue(profile, setLabelQueue);
        } else if (isDifferentLocation(profile)) {
            showLocationConflictAlert(profile);
        } else if (!profile.isSelected) {
            withAllLocationsWarning(() => checkIn(profile, profile.isSelected, profile.isCheckedOut));
        } else {
            checkIn(profile, profile.isSelected, profile.isCheckedOut);
        }
    }, [isAwaitingPrintMode, checkinLocation, checkIn, addPersonToLabelQueue, removePersonFromLabelQueue, isDifferentLocation, showLocationConflictAlert, withAllLocationsWarning]);

    const onFamilyPress = useCallback((profile, mainFamilyMember = null) => {
        if (isAwaitingPrintMode) {
            profile.isPending ? removePersonFromLabelQueue(profile, setLabelQueue) : addPersonToLabelQueue(profile, setLabelQueue);
        } else if (checkinLocation?.printLabelID && !profile.isSelected) {
            profile.isPending ? removePersonFromLabelQueue(profile, setLabelQueue) : addPersonToLabelQueue(profile, setLabelQueue);
        } else if (isDifferentLocation(profile)) {
            showLocationConflictAlert(profile, mainFamilyMember);
        } else if (!profile.isSelected) {
            withAllLocationsWarning(() => checkIn(profile, profile.isSelected, profile.isCheckedOut, mainFamilyMember));
        } else {
            checkIn(profile, profile.isSelected, profile.isCheckedOut, mainFamilyMember);
        }
    }, [isAwaitingPrintMode, checkinLocation, checkIn, addPersonToLabelQueue, removePersonFromLabelQueue, isDifferentLocation, showLocationConflictAlert, withAllLocationsWarning]);

    const onCheckOut = useCallback((profile, mainFamilyMember = null) => {
        checkOut(profile, mainFamilyMember);
    }, [checkOut]);

    const onReprint = useCallback((profile, mode = 'individual') => {
        if (mode === 'individual') {
            printChildLabel({ ...profile, code: profile.labelPrintCode || '-' });
        } else {
            // Family reprint: gather all checked-in family/address members and reprint with same code
            const code = profile.labelPrintCode || generateCode();
            const members = [
                profile,
                ...(profile.Address?.People || []).filter(m => m.isSelected && !m.isCheckedOut),
                ...(profile.Family?.FamilyMembers || []).filter(m => m.isSelected && !m.isCheckedOut && m.id !== profile.id),
            ];
            // Deduplicate by ID
            const uniqueMembers = [...new Map(members.map(m => [m.id, m])).values()];

            const doReprint = async () => {
                for (const member of uniqueMembers) {
                    await printChildLabel({ ...member, code });
                }
                const head_1 = new Set();
                const head_2 = new Set();
                (profile.Family?.FamilyMembers || []).forEach(fm => {
                    if (fm.familyRole === 'Head of Household') head_1.add(`${fm.firstName} ${fm.lastName}`);
                    if (fm.familyRole === 'Spouse') head_2.add(`${fm.firstName} ${fm.lastName}`);
                });
                printParentLabel({
                    members: uniqueMembers,
                    datetime: new Date(),
                    code,
                    class: checkinLocation,
                    head_1: [...head_1].join(', '),
                    head_2: [...head_2].join(', '),
                });
            };
            doReprint();
        }
    }, [printChildLabel, printParentLabel, checkinLocation]);

    // --- Label queue actions ---
    const handleClearPending = useCallback(() => clearAllPending(setLabelQueue), [clearAllPending]);

    const handleCheckInAndPrint = useCallback(() => {
        const doIt = async () => {
        const newCode = generateCode();
        for (const person of labelQueue) {
            checkIn(person, false);
            await printChildLabel({ ...person, code: newCode });
        }
        const head_1 = labelQueue.reduce((acc, p) => {
            const head = p.Family?.FamilyMembers?.find(fm => fm.familyRole === "Head of Household");
            return head ? acc.add(head.firstName + " " + head.lastName) : acc;
        }, new Set());
        const head_2 = labelQueue.reduce((acc, p) => {
            const head = p.Family?.FamilyMembers?.find(fm => fm.familyRole === "Spouse");
            return head ? acc.add(head.firstName + " " + head.lastName) : acc;
        }, new Set());
        // Mark as printed
        markAttendancePrintedMutation.mutate({ attendeeIDs: labelQueue.map(p => p.id), eventInstanceID: eventInstance?.id, labelPrintCode: newCode });
        handleClearPending();
        printParentLabel({ members: labelQueue, datetime: new Date(), code: newCode, class: checkinLocation, head_1: [...head_1].join(", "), head_2: [...head_2].join(", ") });
        };
        withAllLocationsWarning(doIt);
    }, [labelQueue, checkIn, printChildLabel, printParentLabel, checkinLocation, handleClearPending, markAttendancePrintedMutation, eventInstance?.id, withAllLocationsWarning]);

    const handleCheckInOnly = useCallback(() => {
        withAllLocationsWarning(() => {
            labelQueue.forEach(person => checkIn(person, false));
            handleClearPending();
        });
    }, [labelQueue, checkIn, handleClearPending, withAllLocationsWarning]);

    const handlePrintOnly = useCallback(async () => {
        const newCode = generateCode();
        for (const person of labelQueue) {
            await printChildLabel({ ...person, code: newCode });
        }
        const head_1 = labelQueue.reduce((acc, p) => {
            const head = p.Family?.FamilyMembers?.find(fm => fm.familyRole === "Head of Household");
            return head ? acc.add(head.firstName + " " + head.lastName) : acc;
        }, new Set());
        const head_2 = labelQueue.reduce((acc, p) => {
            const head = p.Family?.FamilyMembers?.find(fm => fm.familyRole === "Spouse");
            return head ? acc.add(head.firstName + " " + head.lastName) : acc;
        }, new Set());
        // Mark as printed
        markAttendancePrintedMutation.mutate({ attendeeIDs: labelQueue.map(p => p.id), eventInstanceID: eventInstance?.id, labelPrintCode: newCode });
        handleClearPending();
        printParentLabel({ members: labelQueue, datetime: new Date(), code: newCode, class: checkinLocation, head_1: [...head_1].join(", "), head_2: [...head_2].join(", ") });
    }, [labelQueue, printChildLabel, printParentLabel, checkinLocation, handleClearPending, markAttendancePrintedMutation, eventInstance?.id]);

    const handlePreviewLabels = useCallback(async () => {
        const newCode = 'EX123';
        const previewImages = [];
        for (const person of labelQueue) {
            previewImages.push(await previewChildLabel({ ...person, code: newCode }));
        }
        const head_1 = labelQueue.reduce((acc, p) => {
            const head = p.Family?.FamilyMembers?.find(fm => fm.familyRole === "Head of Household");
            return head ? acc.add(head.firstName + " " + head.lastName) : acc;
        }, new Set());
        
        const head_2 = labelQueue.reduce((acc, p) => {
            const head = p.Family?.FamilyMembers?.find(fm => fm.familyRole === "Spouse");
            return head ? acc.add(head.firstName + " " + head.lastName) : acc;
        }, new Set());
        previewImages.push(await previewParentLabel({ members: labelQueue, datetime: new Date(), code: newCode, class: checkinLocation, head_1: [...head_1].join(", "), head_2: [...head_2].join(", ") }));
        setLabelPreviews(previewImages);
        setPreviewOpen(true);
    }, [labelQueue, previewChildLabel, previewParentLabel, checkinLocation]);

    // --- List footer ---
    const ListFooter = useMemo(() => {
        // Don't show empty state during any loading (initial or refetch)
        if (availablePeople?.length === 0 && !isLoading && !isFetching && !fetchingAttendance) {
            return <View style={{ paddingBottom: 20 }}><EmptyListPlaceholder icon="people-outline" spacing={10} message="No one matching filters." /></View>;
        }
        if (hasNextPage && !kioskModeEnabled) {
            return <View style={{ paddingTop: 25, alignItems: "center" }}><ActivityIndicator size="large" color={colors.primary} /></View>;
        }
        if (kioskModeEnabled && !searchText) {
            return <View style={{ paddingTop: 25 }}><Text style={{ color: colors.textSecondary, textAlign: "center", fontSize: 16 }}>Search for a name, email, phone number, or scan a barcode</Text></View>;
        }
        return <View style={{ paddingTop: 25 }} />;
    }, [availablePeople?.length, hasNextPage, kioskModeEnabled, searchText, colors, isFetching, isLoading, fetchingAttendance]);

    // --- Visible data ---
    const listData = (kioskModeEnabled && searchText === "") ? [] : availablePeople;

    return (
        <View style={styles.topContainer}>
            {kioskModeEnabled &&
                <CheckinKiosk
                    eventInstance={eventInstance}
                    loggedInUser={loggedInUser}
                    checkinLocation={checkinLocation}
                    onCreatePersonPress={openQuickAddPersonModal}
                    onBarcodePress={handleBarcodePress}
                    onExitPress={handleExitKioskPress}
                />
            }

            <CheckinToolbar
                kioskModeEnabled={kioskModeEnabled}
                searchText={searchText}
                setSearchText={setSearchText}
                attendance={attendance}
                checkinLocation={checkinLocation}
                eventInstance={eventInstance}
                selectedFilter={selectedFilter}
                setSelectedFilter={setSelectedFilter}
                setCheckinLocation={setCheckinLocation}
                viewType={viewType}
                setViewType={setViewType}
                labelQueue={labelQueue}
                realPrinters={realPrinters}
                availablePrinters={availablePrinters}
                printers={printers}
                devTestPrinter={devTestPrinter}
                setDevTestPrinter={setDevTestPrinter}
                isPrintingAvailable={isPrintingAvailable}
                onCheckInAndPrint={handleCheckInAndPrint}
                onCheckInOnly={handleCheckInOnly}
                onPrintOnly={handlePrintOnly}
                onPreviewLabels={handlePreviewLabels}
                onClearPending={handleClearPending}
                onOpenAnonymousHeadCountModal={() => setAnonymousHeadCountModalOpen(true)}
                isConnected={isConnected}
                realtimeEnabled={realtimeEnabled}
                onToggleRealtime={handleToggleRealtime}
                isFetching={isFetching}
                // Native-only props for Brother printer:
                selectedPrinter={selectedPrinter}
                onOpenPrinterSheet={openPrinterSheet}
            />

            {viewType === "cards" ? (
                <CheckinCardGrid
                    data={listData}
                    onPersonPress={onProfilePress}
                    onFamilyPress={onFamilyPress}
                    onCheckOut={onCheckOut}
                    onReprint={isPrintingAvailable && realPrinters?.length > 0 ? onReprint : null}
                    listColumns={LIST_COLUMNS}
                    isFetching={isFetching}
                    hasNextPage={hasNextPage}
                    fetchNextPage={fetchNextPage}
                    ListFooter={ListFooter}
                />
            ) : (
                <CheckinTableView
                    data={listData}
                    isLoading={isLoading || fetchingAttendance}
                    onPersonPress={onProfilePress}
                    onFamilyPress={onFamilyPress}
                    onCheckOut={onCheckOut}
                    onEndReached={() => { if (!isFetching && hasNextPage) fetchNextPage(); }}
                    onRefresh={refreshList}
                />
            )}

            {/* --- Modals --- */}
            <PreviewModal isOpen={previewOpen} onClose={() => setPreviewOpen(false)} labelPreviews={labelPreviews} />

            <KioskPinModal
                isOpen={pinOpen} onClose={() => setPinOpen(false)}
                kioskModeEnabled={kioskModeEnabled}
                kioskPin={kioskPin} setKioskPin={setKioskPin}
                kioskPinForUnlock={kioskPinForUnlock} setKioskPinForUnlock={setKioskPinForUnlock}
                storedUnlockCode={storedUnlockCode}
                lockKioskMode={lockKioskMode} unlockKioskMode={unlockKioskMode}
                eventInstanceID={eventInstanceID}
            />

            <BarcodeModal
                isOpen={barcodeOpen} onClose={() => setBarcodeOpen(false)} onReopen={() => setBarcodeOpen(true)}
                barcode={barcode} setBarcode={setBarcode}
                readyToScan={readyToScan} setReadyToScan={setReadyToScan}
                barcodeRef={barcodeRef}
                peopleForNewBarcode={peopleForNewBarcode} setPeopleForNewBarcode={setPeopleForNewBarcode}
                updateBarcodesMutation={updateBarcodesMutation}
                loggedInUser={loggedInUser}
            />

            <QuickAddModal
                isOpen={quickAddOpen}
                onClose={() => setQuickAddOpen(false)}
                onCheckin={(personIDs) => {
                    if (!checkinLocation?.id) return;
                    personIDs.forEach(personID => {
                        addAttendanceMutation.mutate({ eventInstanceID: eventInstance?.id, personID, submitterID: loggedInUser?.id, locationID: checkinLocation.id });
                    });
                }}
            />

            <CheckinPrintSheetModal isOpen={printSheetOpen} onClose={() => setPrintSheetOpen(false)} eventInstance={eventInstance} />

            <CheckinExportModal isOpen={exportModalOpen} onClose={() => setExportModalOpen(false)} onExport={handleExport} checkinFields={CHECKIN_EXPORT_FIELDS} exportCount={totalCount} />

            <AnonymousHeadCountModal
                isOpen={anonymousHeadCountModalOpen}
                onClose={() => setAnonymousHeadCountModalOpen(false)}
                eventInstance={eventInstance}
                attendanceCount={attendance?.length || 0}
                attendance={attendance}
                onUpdate={(anonymousHeadCount) => updateAnonymousHeadCountMutation.mutate({ anonymousHeadCount })}
            />

            {/* --- Off-screen label views for native printing --- */}
            {/* Refs are on plain wrapper Views (not on forwardRef functional components)
                so captureRef gets a direct native View ref that findNodeHandle resolves correctly. */}
            <View
                style={{ position: 'absolute', top: 0, left: 0, width: 1050, height: 330, zIndex:-1, opacity:0.01 }}
                pointerEvents="none"
                collapsable={false}
            >
                <View ref={childLabelRef} style={{ width: 1200, height: 675 }} collapsable={false}>
                    <ChildLabelView data={childLabelData || {}} />
                </View>
                <View ref={familyLabelRef} style={{ width: 1200, height: 675 }} collapsable={false}>
                    <FamilyLabelView data={familyLabelData || {}} />
                </View>
            </View>

            {/* --- Printer selection sheet --- */}
            <PrinterSelectSheet
                isVisible={showPrinterSheet}
                printers={printers}
                isDiscovering={isDiscovering}
                onSelectPrinter={handleSelectPrinter}
                onDismiss={() => setShowPrinterSheet(false)}
                selectedPrinter={selectedPrinter}
                onRefresh={discoverPrinters}
                onConnectToDirectWifi={connectToDirectWifi}
                onConnectByIP={connectByIP}
            />

        </View>
    );
}

const styles = StyleSheet.create(({ colors }) => ({
    topContainer: {
        flex: 1,
        backgroundColor: colors.background,
    },
}));
